import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  loadFixtures,
  loadDirectories,
  putMatchResult,
  putMatchEdit,
  refreshMatch,
  getPrizeSlots,
  putPrizeSlots,
  getTvLinks,
  putTvLinks,
  type MatchSummary,
  type TeamDirectory,
  type StadiumDirectory,
  type PrizeSlot,
  type DrinkSize,
  type TvLink,
} from '../lib/admin';

export function AdminMatches() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [matches, setMatches] = useState<MatchSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'group' | 'r32' | 'r16' | 'qf' | 'sf' | 'third' | 'final'>('all');
  const [openMatch, setOpenMatch] = useState<MatchSummary | null>(null);
  const [editMatch, setEditMatch] = useState<MatchSummary | null>(null);
  const [slotsMatch, setSlotsMatch] = useState<MatchSummary | null>(null);
  const [tvMatch, setTvMatch] = useState<MatchSummary | null>(null);
  const [teams, setTeams] = useState<TeamDirectory[]>([]);
  const [stadiums, setStadiums] = useState<StadiumDirectory[]>([]);
  // Per-row refresh state, keyed by match number.
  const [refreshing, setRefreshing] = useState<number | null>(null);
  const [refreshMsg, setRefreshMsg] = useState<Record<number, { text: string; bad: boolean }>>({});
  // The match awaiting a refresh confirmation, if any.
  const [confirmMatch, setConfirmMatch] = useState<MatchSummary | null>(null);

  async function onRefresh(m: MatchSummary) {
    if (!accessToken || refreshing != null) return;
    setRefreshing(m.number);
    setRefreshMsg((cur) => {
      const next = { ...cur };
      delete next[m.number];
      return next;
    });
    try {
      const res = await refreshMatch(accessToken, m.number);
      let text: string;
      let bad = false;
      if (res.skipped === 'manually_overridden') {
        text = 'Skipped — manual result lock is on (clear override-lock to let sync take over)';
        bad = true;
      } else if (res.errors && res.errors.length) {
        text = `Error: ${res.errors.join('; ')}`;
        bad = true;
      } else if (res.matchesUpdated > 0) {
        text = `Updated · events +${res.eventsInserted}/~${res.eventsUpdated} · stats ${res.statsUpdated}. If now finished, points & leaderboards were recalculated.`;
      } else {
        text = `No change from api-football (events +${res.eventsInserted}/~${res.eventsUpdated}, stats ${res.statsUpdated}).`;
      }
      setRefreshMsg((cur) => ({ ...cur, [m.number]: { text, bad } }));
    } catch (e: any) {
      setRefreshMsg((cur) => ({
        ...cur,
        [m.number]: { text: e?.message || 'Refresh failed', bad: true },
      }));
    } finally {
      setRefreshing(null);
    }
  }

  useEffect(() => {
    if (!accessToken) return;
    loadFixtures(accessToken)
      .then((m) => setMatches(m))
      .catch((e) => setError(`${e}`));
    loadDirectories()
      .then((d) => {
        setTeams(d.teams);
        setStadiums(d.stadiums);
      })
      .catch(() => {/* non-fatal — edit modal will show empty selects */});
  }, [accessToken]);

  const visible = useMemo(() => {
    if (!matches) return [];
    if (filter === 'all') return matches;
    return matches.filter((m) => m.phase === filter);
  }, [matches, filter]);

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Match results</h1>
          </header>

          <div class="admin-toolbar">
            {(['all', 'group', 'r32', 'r16', 'qf', 'sf', 'third', 'final'] as const).map((p) => (
              <button
                key={p}
                class={`admin-chip ${filter === p ? 'on' : ''}`}
                onClick={() => setFilter(p)}
              >
                {p}
              </button>
            ))}
          </div>

          {error && <p class="admin-error">{error}</p>}
          {!matches && !error && <p class="admin-lede">Loading…</p>}

          {matches && (
            <table class="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Phase</th>
                  <th>Group</th>
                  <th>Kickoff (UTC)</th>
                  <th>Home</th>
                  <th>Away</th>
                  <th>Stadium</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                  <tr key={m.number}>
                    <td>{m.number}</td>
                    <td>{m.phase}</td>
                    <td>{m.group ?? '—'}</td>
                    <td class="admin-mono">{m.kickoff_utc.replace('T', ' ').replace('Z', '')}</td>
                    <td>{m.team_a ?? m.team_a_slot ?? '?'}</td>
                    <td>{m.team_b ?? m.team_b_slot ?? '?'}</td>
                    <td>{m.stadium}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button class="admin-link" onClick={() => setEditMatch(m)} style={{ marginRight: 10 }}>
                        Edit
                      </button>
                      <button class="admin-link" onClick={() => setSlotsMatch(m)} style={{ marginRight: 10 }}>
                        Prizes
                      </button>
                      <button class="admin-link" onClick={() => setTvMatch(m)} style={{ marginRight: 10 }}>
                        TV
                      </button>
                      <button
                        class="admin-link"
                        style={{ marginRight: 10 }}
                        disabled={refreshing != null}
                        title="Pull score, stats & status from api-football and, if finished, recompute points + leaderboards"
                        onClick={() => setConfirmMatch(m)}
                      >
                        {refreshing === m.number ? 'Fetching…' : 'Refresh'}
                      </button>
                      <button class="admin-primary" onClick={() => setOpenMatch(m)}>
                        Set result
                      </button>
                      {refreshMsg[m.number] && (
                        <div
                          class={refreshMsg[m.number].bad ? 'admin-error' : 'admin-saved'}
                          style={{ whiteSpace: 'normal', maxWidth: 360, marginTop: 6, fontSize: 12 }}
                        >
                          {refreshMsg[m.number].text}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {confirmMatch && (
            <ConfirmRefreshModal
              match={confirmMatch}
              busy={refreshing === confirmMatch.number}
              onCancel={() => setConfirmMatch(null)}
              onConfirm={() => {
                const m = confirmMatch;
                setConfirmMatch(null);
                onRefresh(m);
              }}
            />
          )}

          {openMatch && accessToken && (
            <ResultModal
              match={openMatch}
              accessToken={accessToken}
              onClose={() => setOpenMatch(null)}
              onSaved={(scoredCount) => {
                setOpenMatch(null);
                console.info(`scored ${scoredCount} predictions`);
              }}
            />
          )}

          {slotsMatch && accessToken && (
            <PrizeSlotsModal
              match={slotsMatch}
              accessToken={accessToken}
              onClose={() => setSlotsMatch(null)}
            />
          )}

          {tvMatch && accessToken && (
            <TvLinksModal
              match={tvMatch}
              accessToken={accessToken}
              onClose={() => setTvMatch(null)}
            />
          )}

          {editMatch && accessToken && (
            <EditModal
              match={editMatch}
              accessToken={accessToken}
              teams={teams}
              stadiums={stadiums}
              onClose={() => setEditMatch(null)}
              onSaved={(updated) => {
                // Patch the row in-place so the table updates without
                // refetching the static bundle (which lives in /public).
                setMatches((cur) => {
                  if (!cur) return cur;
                  return cur.map((m) =>
                    m.number === updated.matchNumber
                      ? {
                          ...m,
                          team_a: updated.teamA || undefined,
                          team_b: updated.teamB || undefined,
                          stadium: updated.stadium,
                          kickoff_utc: updated.kickoffUtc,
                        }
                      : m,
                  );
                });
                setEditMatch(null);
              }}
            />
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

function ConfirmRefreshModal({
  match,
  busy,
  onCancel,
  onConfirm,
}: {
  match: MatchSummary;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const a = match.team_a || match.team_a_slot || 'A';
  const b = match.team_b || match.team_b_slot || 'B';
  return (
    <div class="admin-overlay" onClick={onCancel}>
      <div class="admin-modal" style={{ width: 'min(480px, 100%)' }} onClick={(e) => e.stopPropagation()}>
        <h2>Refresh match #{match.number}?</h2>
        <p class="admin-lede">{a} vs {b} · {match.phase}</p>
        <p class="admin-lede">
          This force-pulls the score, status, events, stats and lineups for this
          match from api-football. The synced timeline is fully reconciled —
          stale auto-imported rows (e.g. a goal api-football later re-spelled)
          are dropped so they match the provider exactly. Any results or events
          you edited by hand in the admin are kept.
        </p>
        <p class="admin-lede">
          If the pull marks the match finished, tournament points, all league
          points, the prize-voucher draw and knockout slots are recomputed.
          Matches under a manual result lock are skipped.
        </p>
        <div class="admin-modal-actions">
          <button type="button" class="admin-link" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" class="admin-primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Fetching…' : 'Refresh now'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({
  match,
  accessToken,
  teams,
  stadiums,
  onClose,
  onSaved,
}: {
  match: MatchSummary;
  accessToken: string;
  teams: TeamDirectory[];
  stadiums: StadiumDirectory[];
  onClose: () => void;
  onSaved: (updated: { matchNumber: number; teamA?: string; teamB?: string; stadium: string; kickoffUtc: string }) => void;
}) {
  const [teamA, setTeamA] = useState(match.team_a ?? '');
  const [teamB, setTeamB] = useState(match.team_b ?? '');
  const [stadium, setStadium] = useState(match.stadium);
  // datetime-local wants YYYY-MM-DDTHH:MM (no seconds, no zone). The kickoff
  // is RFC3339 UTC — strip the seconds + Z for display, re-add on save.
  const [kickoff, setKickoff] = useState(
    match.kickoff_utc.replace('Z', '').replace(/:\d\d(\.\d+)?$/, ''),
  );
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      // Compose the payload — only send fields the user actually changed.
      const payload: any = {};
      if (teamA !== (match.team_a ?? '')) payload.teamA = teamA;
      if (teamB !== (match.team_b ?? '')) payload.teamB = teamB;
      if (stadium !== match.stadium) payload.stadium = stadium;
      const origKick = match.kickoff_utc.replace('Z', '').replace(/:\d\d(\.\d+)?$/, '');
      if (kickoff !== origKick) {
        // datetime-local string → "YYYY-MM-DDTHH:MM" → add ":00Z" for UTC.
        payload.kickoffUtc = `${kickoff}:00Z`;
      }
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }
      const updated = await putMatchEdit(accessToken, match.number, payload);
      onSaved(updated);
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <form class="admin-modal" style={{ width: 'min(560px, 100%)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>Edit match #{match.number}</h2>
        <p class="admin-lede">Override teams, stadium or kickoff. Result entry stays on the other button.</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <label class="admin-field">
            <span>Team A (country code)</span>
            <select value={teamA} onChange={(e) => setTeamA((e.target as HTMLSelectElement).value)}>
              <option value="">— TBD —</option>
              {teams.map((t) => <option key={t.code} value={t.code}>{t.code} · {t.name}</option>)}
            </select>
          </label>
          <label class="admin-field">
            <span>Team B (country code)</span>
            <select value={teamB} onChange={(e) => setTeamB((e.target as HTMLSelectElement).value)}>
              <option value="">— TBD —</option>
              {teams.map((t) => <option key={t.code} value={t.code}>{t.code} · {t.name}</option>)}
            </select>
          </label>
        </div>

        <label class="admin-field">
          <span>Stadium</span>
          <select value={stadium} onChange={(e) => setStadium((e.target as HTMLSelectElement).value)}>
            {stadiums.map((s) => <option key={s.code} value={s.code}>{s.code} · {s.city} ({s.name})</option>)}
          </select>
        </label>

        <label class="admin-field">
          <span>Kickoff (UTC)</span>
          <input
            type="datetime-local"
            value={kickoff}
            onInput={(e) => setKickoff((e.target as HTMLInputElement).value)}
          />
        </label>

        {err && <p class="admin-error">{err}</p>}

        <div class="admin-modal-actions">
          <button type="button" class="admin-link" onClick={onClose}>Cancel</button>
          <button type="submit" class="admin-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ResultModal({
  match,
  accessToken,
  onClose,
  onSaved,
}: {
  match: MatchSummary;
  accessToken: string;
  onClose: () => void;
  onSaved: (scored: number) => void;
}) {
  const [scoreA, setScoreA] = useState('0');
  const [scoreB, setScoreB] = useState('0');
  const [winnerCode, setWinnerCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isKo = match.phase !== 'group';
  const a = match.team_a || match.team_a_slot || 'A';
  const b = match.team_b || match.team_b_slot || 'B';

  async function submit(e: Event) {
    e.preventDefault();
    const sa = parseInt(scoreA, 10);
    const sb = parseInt(scoreB, 10);
    if (isNaN(sa) || isNaN(sb) || sa < 0 || sb < 0 || sa > 99 || sb > 99) {
      setErr('Scores must be 0–99');
      return;
    }
    if (isKo && sa === sb && !winnerCode.trim()) {
      setErr('Knockout draw needs a winner code (e.g. BRA)');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const res = await putMatchResult(accessToken, match.number, {
        scoreA: sa,
        scoreB: sb,
        winnerCode: isKo && sa === sb ? winnerCode.trim().toUpperCase() : undefined,
      });
      onSaved(res.scored);
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <form class="admin-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>Match #{match.number} — {a} vs {b}</h2>
        <p class="admin-lede">{match.phase} · {match.stadium} · {match.kickoff_utc.replace('T', ' ').replace('Z', '')}</p>

        <div class="admin-score-row">
          <label>
            <span>{a}</span>
            <input
              type="number"
              min={0}
              max={99}
              value={scoreA}
              onInput={(e) => setScoreA((e.target as HTMLInputElement).value)}
            />
          </label>
          <span class="admin-score-sep">:</span>
          <label>
            <span>{b}</span>
            <input
              type="number"
              min={0}
              max={99}
              value={scoreB}
              onInput={(e) => setScoreB((e.target as HTMLInputElement).value)}
            />
          </label>
        </div>

        {isKo && parseInt(scoreA, 10) === parseInt(scoreB, 10) && (
          <label class="admin-field">
            <span>Winner after penalties (3-letter code)</span>
            <input
              type="text"
              maxLength={3}
              placeholder="e.g. BRA"
              value={winnerCode}
              onInput={(e) => setWinnerCode((e.target as HTMLInputElement).value)}
            />
          </label>
        )}

        {err && <p class="admin-error">{err}</p>}

        <div class="admin-modal-actions">
          <button type="button" class="admin-link" onClick={onClose}>Cancel</button>
          <button type="submit" class="admin-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save result'}
          </button>
        </div>
      </form>
    </div>
  );
}

const DRINK_SIZES: { id: DrinkSize; label: string }[] = [
  { id: 'pint', label: 'Pint' },
  { id: 'half', label: '0.5L' },
  { id: 'third', label: '0.33L' },
];

function PrizeSlotsModal({
  match,
  accessToken,
  onClose,
}: {
  match: MatchSummary;
  accessToken: string;
  onClose: () => void;
}) {
  const [counts, setCounts] = useState<Record<DrinkSize, string>>({
    pint: '0',
    half: '0',
    third: '0',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getPrizeSlots(accessToken, match.number)
      .then((slots) => {
        const next: Record<DrinkSize, string> = { pint: '0', half: '0', third: '0' };
        for (const s of slots) next[s.drinkSize] = String(s.count);
        setCounts(next);
      })
      .catch((e: any) => setErr(`${e?.message || e}`))
      .finally(() => setLoading(false));
  }, [accessToken, match.number]);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSaved(false);
    setSaving(true);
    try {
      const out: PrizeSlot[] = [];
      for (const k of ['pint', 'half', 'third'] as DrinkSize[]) {
        const n = parseInt(counts[k] || '0', 10);
        if (isNaN(n) || n < 0) throw new Error(`Count for ${k} must be a non-negative integer`);
        if (n > 0) out.push({ drinkSize: k, count: n });
      }
      await putPrizeSlots(accessToken, match.number, out);
      setSaved(true);
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <form class="admin-modal" style={{ width: 'min(420px, 100%)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>Prize slots · match #{match.number}</h2>
        <p class="admin-lede">
          How many drinks of each size are up for grabs on this match. Vouchers are
          drawn by weighted lottery when the result is entered — better scores
          weigh more, but a comeback boost helps users who haven't won recently.
        </p>

        {loading ? (
          <p class="admin-lede">Loading…</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            {DRINK_SIZES.map((d) => (
              <label key={d.id} class="admin-field">
                <span>{d.label}</span>
                <input
                  type="number"
                  min={0}
                  value={counts[d.id]}
                  onInput={(e) =>
                    setCounts({ ...counts, [d.id]: (e.target as HTMLInputElement).value })
                  }
                />
              </label>
            ))}
          </div>
        )}

        {err && <p class="admin-error">{err}</p>}
        {saved && <p class="admin-saved">Saved.</p>}

        <div class="admin-modal-actions">
          <button type="button" class="admin-link" onClick={onClose}>Close</button>
          <button type="submit" class="admin-primary" disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save slots'}
          </button>
        </div>
      </form>
    </div>
  );
}

function emptyTvRow(): TvLink {
  return { channel: '', region: '', language: '', note: '', url: '', isFree: true };
}

function TvLinksModal({
  match,
  accessToken,
  onClose,
}: {
  match: MatchSummary;
  accessToken: string;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<TvLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getTvLinks(accessToken, match.number)
      .then((list) => setRows(list))
      .catch((e: any) => setErr(`${e?.message || e}`))
      .finally(() => setLoading(false));
  }, [accessToken, match.number]);

  function setRow(i: number, patch: Partial<TvLink>) {
    setRows((cur) => cur.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function removeRow(i: number) {
    setRows((cur) => cur.filter((_, idx) => idx !== i));
  }
  function moveRow(i: number, delta: number) {
    setRows((cur) => {
      const next = [...cur];
      const j = i + delta;
      if (j < 0 || j >= next.length) return cur;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSaved(false);
    // Drop rows the admin left fully blank (common when they hit "+ Add" then
    // changed their mind) before validating — those would error out otherwise.
    const cleaned = rows.filter((r) => (r.channel ?? '').trim() !== '');
    setSaving(true);
    try {
      const persisted = await putTvLinks(
        accessToken,
        match.number,
        cleaned.map((r, i) => ({ ...r, sortOrder: i })),
      );
      setRows(persisted);
      setSaved(true);
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <form
        class="admin-modal"
        style={{ width: 'min(680px, 100%)' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2>TV broadcasts · match #{match.number}</h2>
        <p class="admin-lede">
          Channels to show in the app's "Dove guardarla" tab. The tab is hidden
          on this match if you save zero rows. Region is the language area
          (IT/DE/FR) or country code; note is the optional caption
          (e.g. "HD broadcast", "Live commentary").
        </p>

        {loading ? (
          <p class="admin-lede">Loading…</p>
        ) : (
          <>
            {rows.length === 0 && (
              <p class="admin-lede">No broadcasts configured yet — the TV tab will be hidden.</p>
            )}
            {rows.map((r, i) => (
              <div
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 0.7fr 0.7fr 1.4fr 1.6fr auto auto auto',
                  gap: '6px',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <input
                  placeholder="Channel (e.g. RSI La 2)"
                  value={r.channel}
                  onInput={(e) => setRow(i, { channel: (e.target as HTMLInputElement).value })}
                />
                <input
                  placeholder="Region"
                  value={r.region ?? ''}
                  maxLength={8}
                  onInput={(e) => setRow(i, { region: (e.target as HTMLInputElement).value })}
                />
                <input
                  placeholder="Lang"
                  value={r.language ?? ''}
                  maxLength={8}
                  onInput={(e) => setRow(i, { language: (e.target as HTMLInputElement).value })}
                />
                <input
                  placeholder="Note (HD, Live, …)"
                  value={r.note ?? ''}
                  onInput={(e) => setRow(i, { note: (e.target as HTMLInputElement).value })}
                />
                <input
                  placeholder="URL (optional)"
                  value={r.url ?? ''}
                  onInput={(e) => setRow(i, { url: (e.target as HTMLInputElement).value })}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={r.isFree}
                    onChange={(e) => setRow(i, { isFree: (e.target as HTMLInputElement).checked })}
                  />
                  Free
                </label>
                <div style={{ display: 'flex', gap: 2 }}>
                  <button type="button" class="admin-link" onClick={() => moveRow(i, -1)} disabled={i === 0}>↑</button>
                  <button type="button" class="admin-link" onClick={() => moveRow(i, 1)} disabled={i === rows.length - 1}>↓</button>
                </div>
                <button type="button" class="admin-link" onClick={() => removeRow(i)}>✕</button>
              </div>
            ))}
            <button
              type="button"
              class="admin-link"
              onClick={() => setRows((cur) => [...cur, emptyTvRow()])}
            >
              + Add channel
            </button>
          </>
        )}

        {err && <p class="admin-error">{err}</p>}
        {saved && <p class="admin-saved">Saved.</p>}

        <div class="admin-modal-actions">
          <button type="button" class="admin-link" onClick={onClose}>Close</button>
          <button type="submit" class="admin-primary" disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save broadcasts'}
          </button>
        </div>
      </form>
    </div>
  );
}
